import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiDollarSign,
  FiPackage,
  FiArrowLeft,
  FiPhone,
  FiMapPin,
  FiMail,
  FiCreditCard,
  FiFileText,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiTrash2
} from 'react-icons/fi';
import axios from 'axios';
import ConfirmationModal from '../components/confirmation';
import './supplierDetails.css';

const SupplierDetailsPage = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({
    supplier: null,
    payments: [],
    purchases: [],
    loading: true,
    error: null
  });
  const [filteredData, setFilteredData] = useState({
    payments: [],
    purchases: []
  });
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [expandedPurchases, setExpandedPurchases] = useState({});
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadStatement = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      alert('Please select a start and end date first.');
      return;
    }
    if (!data.supplier) {
      alert('Supplier data not loaded yet.');
      return;
    }

    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `/suppliers/${supplierId}/statement/pdf`,
        {
          params: { start: dateRange.startDate, end: dateRange.endDate },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `statement-${data.supplier.name.replace(/\s+/g, '_')}-${dateRange.startDate}-to-${dateRange.endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading statement:', err);
      alert('Failed to generate statement PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const fetchData = async () => {
    try {
      setData(prev => ({...prev, loading: true}));
      const response = await axios.get(
        `/suppliers/details/${supplierId}`
      );
      
      setData({
        supplier: response.data.supplier,
        payments: response.data.payments || [],
        purchases: response.data.purchases || [],
        loading: false,
        error: null
      });
      setFilteredData({
        payments: response.data.payments || [],
        purchases: response.data.purchases || []
      });
    } catch (err) {
      setData({
        supplier: null,
        payments: [],
        purchases: [],
        loading: false,
        error: err.response?.data?.error || 'Failed to load data'
      });
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  const handleDeletePaymentClick = (payment) => {
    setPaymentToDelete(payment);
  };

  const handleConfirmDeletePayment = async () => {
    if (!paymentToDelete) return;
    try {
      setDeletingPayment(true);
      await axios.delete(`/suppliers/${supplierId}/payments/${paymentToDelete.payment_id}`);
      await fetchData();
    } catch (err) {
      console.error('Error deleting payment:', err);
      alert(err.response?.data?.error || 'Failed to delete payment.');
    } finally {
      setDeletingPayment(false);
      setPaymentToDelete(null);
    }
  };

  const handleCancelDeletePayment = () => {
    setPaymentToDelete(null);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilter = (e) => {
    e.preventDefault();
    const { startDate, endDate } = dateRange;
    
    if (!startDate || !endDate) {
      setFilteredData({
        payments: data.payments,
        purchases: data.purchases
      });
      return;
    }

    const filteredPayments = data.payments.filter(payment => {
      const paymentDate = new Date(payment.payment_date);
      return paymentDate >= new Date(startDate) && paymentDate <= new Date(endDate);
    });

    const filteredPurchases = data.purchases.filter(purchase => {
      const purchaseDate = new Date(purchase.purchase_date);
      return purchaseDate >= new Date(startDate) && purchaseDate <= new Date(endDate);
    });

    setFilteredData({
      payments: filteredPayments,
      purchases: filteredPurchases
    });
  };

  const handleClearFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
    setFilteredData({
      payments: data.payments,
      purchases: data.purchases
    });
  };

  const togglePurchaseDetails = (purchaseDate) => {
    setExpandedPurchases(prev => ({
      ...prev,
      [purchaseDate]: !prev[purchaseDate]
    }));
  };

  const { supplier, payments, purchases, loading, error } = data;
  const { payments: filteredPayments, purchases: filteredPurchases } = filteredData;

  // Calculate financial summaries using direct due_amount from supplier
  const totalPaymentsValue = payments.reduce(
    (sum, payment) => sum + (parseFloat(payment.amount) || 0), 0
  );
  const totalPurchases = supplier?.total_purchases || 0;
  const balanceDue = supplier ? supplier.due_amount : 0;


  // Group purchases by date for display
  const purchasesByDate = filteredPurchases.reduce((acc, purchase) => {
    const date = new Date(purchase.purchase_date).toLocaleDateString('en-IN');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(purchase);
    return acc;
  }, {});

  const purchaseSummaries = Object.entries(purchasesByDate).map(([date, purchases], index) => {
    const total = purchases.reduce((sum, purchase) => sum + parseFloat(purchase.total_amount), 0);
    return {
      sn: index + 1,
      date,
      purchases,
      total
    };
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  if (loading) return <div className="loading">Loading supplier details...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!supplier) return <div className="error">Supplier not found</div>;

  return (
    <div className="supplier-details-container">
      <div className="header">
        <button onClick={() => navigate('/suppliers')} className="back-button">
          <FiArrowLeft /> Back to Suppliers
        </button>
        <h1>{supplier.name}</h1>
        <div className="supplier-info">
          <p><FiPhone /> {supplier.contact_number}</p>
          {supplier.email && <p><FiMail /> {supplier.email}</p>}
          {supplier.address && <p><FiMapPin /> {supplier.address}</p>}
        </div>
      </div>

      <div className="financial-summary">
        <div className="summary-card">
    <h3><FiPackage /> Total Purchases</h3>
    <p>Rs {totalPurchases.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
  </div>
        <div className="summary-card">
          <h3><FiDollarSign /> Total Payments</h3>
          <p>Rs {totalPaymentsValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className={`summary-card ${balanceDue > 0 ? 'due' : 'paid'}`}>
    <h3>Balance Due</h3>
    <p>
      Rs {Math.abs(balanceDue).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
      {balanceDue < 0 && ' (Credit Balance)'}
    </p>
  </div>
      </div>

      <div className="date-filter-compact">
        <form onSubmit={handleFilter}>
          <h3><FiCalendar /> Select date:</h3>
          <input
            type="date"
            name="startDate"
            value={dateRange.startDate}
            onChange={handleDateChange}
            placeholder="From"
          />
          <input
            type="date"
            name="endDate"
            value={dateRange.endDate}
            onChange={handleDateChange}
            min={dateRange.startDate}
            placeholder="To"
          />
          <button type="submit" title="Apply Filter">🔍</button>
          <button
            type="button"
            onClick={handleClearFilter}
            title="Clear Filter"
          >
            ❌
          </button>
          <button
            type="button"
            onClick={handleDownloadStatement}
            disabled={downloadingPdf}
            className="download-statement-btn"
            title="Download PDF Statement"
          >
            {downloadingPdf ? 'Generating...' : '📄 Download Statement'}
          </button>
        </form>
      </div>

      <div className="details-section">
        <div className="purchases-section">
          <h2>Purchase History</h2>
          {purchaseSummaries.length > 0 ? (
            <table className="purchases-table">
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Date</th>
                  <th>Total Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {purchaseSummaries.map((purchase) => (
                  <React.Fragment key={purchase.date}>
                    <tr>
                      <td>{purchase.sn}</td>
                      <td>{purchase.date}</td>
                      <td>Rs {purchase.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>
                        <button 
                          onClick={() => togglePurchaseDetails(purchase.date)}
                          className="view-details-btn"
                        >
                          {expandedPurchases[purchase.date] ? (
                            <>
                              <FiChevronUp /> Hide Details
                            </>
                          ) : (
                            <>
                              <FiChevronDown /> View Details
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedPurchases[purchase.date] && (
                      <tr className="purchase-details-row">
                        <td colSpan="4">
                          <table className="items-details-table">
                            <thead>
                              <tr>
                                <th>Item Name</th>
                                <th>Quantity</th>
                                <th>Unit</th>
                                <th>Unit Cost</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {purchase.purchases.map(p => (
                                <tr key={p.purchase_id}>
                                  <td>{p.item_name}</td>
                                  <td>{p.quantity}</td>
                                  <td>{p.unit}</td>
                                  <td>Rs {p.cost_price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                  <td>Rs {p.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-data">
              {filteredPurchases.length === 0 && data.purchases.length > 0 
                ? 'No purchases found for selected date range' 
                : 'No purchases found from this supplier'}
            </div>
          )}
        </div>

        <div className="payments-section">
          <h2>Payment History</h2>
          {filteredPayments.length > 0 ? (
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Receipt No</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map(payment => (
                  <tr key={payment.payment_id}>
                    <td>{new Date(payment.payment_date).toLocaleDateString('en-IN')}</td>
                    <td>Rs {payment.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td>
                      <span className={`payment-type ${payment.payment_type}`}>
                        <FiCreditCard className="payment-icon" />
                        {payment.payment_type || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div className="receipt-info">
                        <FiFileText className="receipt-icon" />
                        {payment.receipt_no || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="delete-btn"
                        title="Delete payment"
                        onClick={() => handleDeletePaymentClick(payment)}
                        disabled={deletingPayment}
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-data">
              {filteredPayments.length === 0 && data.payments.length > 0
                ? 'No payments found for selected date range'
                : 'No payments recorded'}
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        show={!!paymentToDelete}
        title="Delete Payment"
        message={
          paymentToDelete
            ? `Are you sure you want to delete this Rs ${paymentToDelete.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })} payment from ${new Date(paymentToDelete.payment_date).toLocaleDateString('en-IN')}? This will add the amount back to the supplier's due balance.`
            : ''
        }
        onConfirm={handleConfirmDeletePayment}
        onCancel={handleCancelDeletePayment}
      />
    </div>
  );
};

export default SupplierDetailsPage;